<template>
    <div>
        <ui-section v-if="loading || items.length" flush class="mb-4">
            <template #header>
                <h3 class="ui-section-title">
                    Associações pendentes
                    <span class="ui-tab-count ui-tab-count--attention">{{ total }}</span>
                </h3>
                <p class="ui-section-description">Pedidos de procuradores para representar credores, aguardando análise.</p>
            </template>
            <template #actions>
                <v-btn small outlined :loading="loading" @click="load">
                    <v-icon left small>mdi-refresh</v-icon>
                    Atualizar
                </v-btn>
            </template>

            <!-- Todos os pedidos pendentes, paginados no servidor; o usuario escolhe quantos ver por pagina -->
            <v-data-table
                :headers="headers"
                :items="items"
                :loading="loading"
                disable-sort
                hide-default-footer
                :server-items-length="total"
                :options.sync="options"
                class="ui-clickable-rows"
                no-data-text="Nenhuma associação pendente encontrada"
                loading-text="Carregando associações pendentes..."
                @click:row="openProcurator"
            >
                <template v-slot:[`item.procurator`]="{ item }">
                    <div class="ui-cell-main">{{ getProcuratorName(item) }}</div>
                    <div class="ui-cell-sub">{{ getProcuratorEmail(item) }}</div>
                </template>

                <template v-slot:[`item.creditor_name`]="{ item }">
                    <div class="ui-cell-main">{{ item.creditor_name || 'N/A' }}</div>
                    <div class="ui-cell-sub">{{ formatRegistration(item.creditor_registration) }}</div>
                </template>

                <template v-slot:[`item.client`]="{ item }">
                    {{ item.client ? item.client.name : 'N/A' }}
                </template>

                <template v-slot:[`item.created_at`]="{ item }">
                    {{ item.created_at | defaultDate("DD/MM/YYYY HH:mm") }}
                </template>

                <template v-slot:[`item.documents`]="{ item }">
                    <div class="d-flex align-center" style="gap: 6px;">
                        <v-tooltip bottom>
                            <template #activator="{ on }">
                                <v-btn v-if="item.creditor_document_url" icon small :href="item.creditor_document_url" target="_blank" v-on="on" @click.stop>
                                    <v-icon size="18">mdi-card-account-details-outline</v-icon>
                                </v-btn>
                            </template>
                            <span>Documento de ID do credor</span>
                        </v-tooltip>
                        <v-tooltip bottom>
                            <template #activator="{ on }">
                                <v-btn v-if="item.procuration_document_url" icon small :href="item.procuration_document_url" target="_blank" v-on="on" @click.stop>
                                    <v-icon size="18">mdi-file-document-outline</v-icon>
                                </v-btn>
                            </template>
                            <span>Procuração</span>
                        </v-tooltip>
                        <!-- Procuracao gerada pelo modelo: versao e validacao -->
                        <procuration-source-badge v-if="item.procuration" :procuration="item.procuration" />
                    </div>
                </template>

                <template v-slot:[`item.procuration_powers`]="{ item }">
                    <div v-if="item.procuration_powers && item.procuration_powers.length" class="d-flex flex-wrap" style="gap: 4px;">
                        <ui-badge v-for="power in item.procuration_powers" :key="power" :text="powerLabel(power)" tone="primary" :dot="false" />
                    </div>
                    <span v-else class="ui-small ui-muted">Poderes não informados</span>
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                    <ui-row-actions :actions="[
                        { text: 'Ver procurador', icon: 'mdi-eye-outline', handler: () => openProcurator(item) },
                        { text: 'Aprovar associação', icon: 'mdi-check-circle-outline', handler: () => openApproveDialog(item) },
                        { text: 'Solicitar regularização', icon: 'mdi-file-document-refresh-outline', handler: () => openRejectDialog(item, 'regularize') },
                        { text: 'Rejeitar associação', icon: 'mdi-close-circle-outline', handler: () => openRejectDialog(item), danger: true },
                    ]" />
                </template>

                <template #footer>
                    <ui-pagination :total="total" :page="options.page" :items-per-page="options.itemsPerPage"
                        :items-per-page-options="footerOptions.itemsPerPageOptions" :disabled="loading"
                        @update:page="options.page = $event" @update:itemsPerPage="options.itemsPerPage = $event" />
                </template>
            </v-data-table>
        </ui-section>

        <v-dialog v-model="approveDialog.open" max-width="760px" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">Aprovar associação</h3>
                        <p class="ui-section-description">Escolha se o procurador é adicionado ou substitui os aprovados.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <div class="mb-4 text-body-2">
                        Revise a associação com o credor
                        <strong>{{ approveDialog.item ? approveDialog.item.creditor_name : '' }}</strong>.
                    </div>

                    <v-radio-group v-model="approveDialog.mode" row>
                        <v-radio label="Adicionar procurador" value="add" />
                        <v-radio label="Substituir procuradores aprovados" value="replace" />
                    </v-radio-group>

                    <v-alert dense text type="info">
                        Se houver outros procuradores na mesma procuração, marque apenas os nomes autorizados.
                    </v-alert>

                    <v-simple-table v-if="approveDialog.authorizedProcurators.length" dense>
                        <thead>
                            <tr>
                                <th>Autorizar</th>
                                <th>Nome</th>
                                <th>CPF</th>
                                <th>E-mail</th>
                                <th>OAB</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(procurator, index) in approveDialog.authorizedProcurators" :key="`authorized-${index}`">
                                <td>
                                    <v-checkbox v-model="procurator.approved" dense hide-details />
                                </td>
                                <td>{{ procurator.name }}</td>
                                <td>{{ formatRegistration(procurator.registration) }}</td>
                                <td>{{ procurator.email || 'N/A' }}</td>
                                <td>{{ procurator.oab ? `${procurator.oab}/${procurator.oab_state || ''}` : 'N/A' }}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                    <div v-else class="text-body-2 ui-muted">
                        Nenhum procurador adicional informado nesta solicitação.
                    </div>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="approveDialog.loading" @click="closeApproveDialog">
                        Cancelar
                    </v-btn>
                    <v-btn v-if="$allowed('procurator.associations')" color="primary" depressed :loading="approveDialog.loading" @click="confirmApprove">
                        Aprovar
                    </v-btn>
                </div>
            </v-card>
        </v-dialog>

        <v-dialog v-model="rejectDialog.open" max-width="600px" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">{{ rejectDialog.mode === 'regularize' ? 'Solicitar regularização' : 'Rejeitar associação' }}</h3>
                        <p class="ui-section-description">O motivo informado fica registrado na solicitação.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <div class="mb-4 text-body-2">
                        {{ rejectDialog.mode === 'regularize' ? 'Informe o que o procurador deve complementar no pedido de associação com o credor' : 'Informe o motivo da rejeição para a associação com o credor' }}
                        <strong>{{ rejectDialog.item ? rejectDialog.item.creditor_name : '' }}</strong>.
                    </div>
                    <v-textarea
                        v-model="rejectDialog.reason"
                        :label="rejectDialog.mode === 'regularize' ? 'O que deve ser regularizado' : 'Motivo da rejeição'"
                        outlined
                        auto-grow
                        rows="3"
                        hide-details="auto"
                    />
                    <v-alert v-if="rejectDialog.mode === 'regularize'" dense text type="info" class="mt-4 mb-0 text-body-2">
                        O procurador recebe o pedido por e-mail e complementa este mesmo pedido em "Complementar". A data original da solicitação é mantida.
                    </v-alert>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="rejectDialog.loading" @click="closeRejectDialog">
                        Cancelar
                    </v-btn>
                    <v-btn v-if="$allowed('procurator.associations')" color="primary" depressed :loading="rejectDialog.loading" @click="confirmReject">
                        {{ rejectDialog.mode === 'regularize' ? 'Solicitar regularização' : 'Rejeitar' }}
                    </v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import procurationPowers from "@/common/data/procuration-powers.data";
import axios from "axios";
import ProcurationSourceBadge from "@/components/ProcurationSourceBadge.vue";

export default {
    name: "PendingProcuratorAssociations",

    components: { ProcurationSourceBadge },

    data: () => ({
        loading: false,
        items: [],
        total: 0,
        options: {
            page: 1,
            itemsPerPage: 10,
        },
        // Ultima pagina/quantidade carregada: evita recarregar quando a tabela apenas sincroniza as opcoes
        loaded: { page: null, itemsPerPage: null },
        footerOptions: {
            itemsPerPageText: 'Pendentes por página:',
            itemsPerPageOptions: [5, 10, 20, 50, 100],
            pageText: '{0}-{1} de {2}',
        },
        headers: [
            { text: "Procurador", value: "procurator" },
            { text: "Credor", value: "creditor_name" },
            { text: "Caso", value: "client" },
            { text: "Solicitado em", value: "created_at" },
            { text: "Documentos", value: "documents", sortable: false },
            { text: "Poderes", value: "procuration_powers", sortable: false },
            { text: "", value: "actions", sortable: false, align: "end", width: 56 },
        ],
        rejectDialog: {
            open: false,
            loading: false,
            item: null,
            reason: "",
            mode: "reject",
        },
        approveDialog: {
            open: false,
            loading: false,
            item: null,
            mode: "add",
            authorizedProcurators: [],
        },
    }),

    created() {
        this.load();
    },

    watch: {
        // Troca de pagina ou de quantidade por pagina recarrega do servidor
        options: {
            handler(value) {
                if (value.page === this.loaded.page && value.itemsPerPage === this.loaded.itemsPerPage) return;
                this.load();
            },
            deep: true,
        },
    },

    methods: {
        powerLabel(id) {
            const found = procurationPowers.find((item) => item.id === id);
            return found ? found.name : id;
        },

        async load() {
            this.loading = true;

            try {
                const result = await axios.get("/api/admin/procurator-associations", {
                    params: {
                        page: this.options.page,
                        limit: this.options.itemsPerPage,
                        search: { status: 0 },
                    },
                });

                this.items = result.data.content.data || [];
                this.total = result.data.content.paging.total || 0;
                this.loaded = { page: this.options.page, itemsPerPage: this.options.itemsPerPage };

                // Pagina ficou vazia (ex.: ultimo pendente aprovado): volta uma pagina.
                if (!this.items.length && this.options.page > 1) {
                    this.options.page = Math.max(1, Math.ceil(this.total / this.options.itemsPerPage) || 1);
                }
            } catch (error) {
                this.items = [];
                this.total = 0;
                this.$dialog.notify.error(this.$codes(error), {
                    position: "top-right",
                    timeout: 3000,
                });
            } finally {
                this.loading = false;
            }
        },

        getProcuratorName(item) {
            return item.procurator && item.procurator.name ? item.procurator.name : "N/A";
        },

        getProcuratorEmail(item) {
            return item.procurator && item.procurator.email ? item.procurator.email : "N/A";
        },

        openProcurator(item) {
            if (!item.procurator_user_id) return;
            this.$router.push(`/procuradores/${item.procurator_user_id}`);
        },

        parseAuthorizedProcurators(value) {
            return Array.isArray(value) ? value : [];
        },

        openApproveDialog(item) {
            this.approveDialog.item = item;
            this.approveDialog.mode = "add";
            this.approveDialog.authorizedProcurators = this.parseAuthorizedProcurators(item.authorized_procurators)
                .map((procurator) => ({
                    ...procurator,
                    approved: procurator.approved !== false,
                }));
            this.approveDialog.open = true;
        },

        closeApproveDialog() {
            if (this.approveDialog.loading) return;
            this.approveDialog.open = false;
            this.approveDialog.item = null;
            this.approveDialog.mode = "add";
            this.approveDialog.authorizedProcurators = [];
        },

        async confirmApprove() {
            if (!this.approveDialog.item) return;
            this.approveDialog.loading = true;

            try {
                await axios.post(`/api/admin/procurator-associations/${this.approveDialog.item.id}/approve`, {
                    approval_mode: this.approveDialog.mode,
                    replace_existing: this.approveDialog.mode === "replace",
                    authorized_procurators: JSON.stringify(this.approveDialog.authorizedProcurators),
                });

                this.$dialog.notify.info("Associação aprovada com sucesso!", { position: "top-right", timeout: 2000 });
                this.approveDialog.open = false;
                this.approveDialog.item = null;
                this.approveDialog.mode = "add";
                this.approveDialog.authorizedProcurators = [];
                await this.load();
                this.$emit("updated");
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: "top-right",
                    timeout: 3000,
                });
            } finally {
                this.approveDialog.loading = false;
            }
        },

        openRejectDialog(item, mode = "reject") {
            this.rejectDialog.item = item;
            this.rejectDialog.mode = mode;
            this.rejectDialog.reason = "";
            this.rejectDialog.open = true;
        },

        closeRejectDialog() {
            if (this.rejectDialog.loading) return;
            this.rejectDialog.open = false;
            this.rejectDialog.item = null;
            this.rejectDialog.reason = "";
        },

        async confirmReject() {
            if (!this.rejectDialog.item) return;
            const regularize = this.rejectDialog.mode === "regularize";
            if (!this.rejectDialog.reason || !this.rejectDialog.reason.trim()) {
                this.$dialog.notify.error(regularize ? "Informe o que deve ser regularizado" : "Motivo da rejeição é obrigatório", {
                    position: "top-right",
                    timeout: 3000,
                });
                return;
            }

            this.rejectDialog.loading = true;

            try {
                await axios.post(`/api/admin/procurator-associations/${this.rejectDialog.item.id}/${regularize ? "request-regularization" : "reject"}`, {
                    [regularize ? "reason" : "rejection_reason"]: this.rejectDialog.reason.trim(),
                });

                this.$dialog.notify.info(regularize ? "Regularização solicitada ao procurador." : "Associação rejeitada com sucesso!", { position: "top-right", timeout: 2000 });
                this.closeRejectDialog();
                await this.load();
                this.$emit("updated");
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: "top-right",
                    timeout: 3000,
                });
            } finally {
                this.rejectDialog.loading = false;
            }
        },

        formatRegistration(registration) {
            const value = String(registration || "").replace(/\D/g, "");
            if (value.length === 11) {
                return value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
            }
            if (value.length === 14) {
                return value.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
            }
            return registration || "N/A";
        },
    },
};
</script>
